import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Chip,
  Box,
  InputAdornment,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import SearchIcon from '@mui/icons-material/Search';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { Recipe } from '../../types';

interface FavouritesPickerDialogProps {
  open: boolean;
  handleClose: () => void;
  onSelectRecipe: (recipe: Recipe) => void;
  recipes: Recipe[];
  excludeRecipeIds?: string[];
}

export default function FavouritesPickerDialog(props: FavouritesPickerDialogProps) {
  const [searchFilter, setSearchFilter] = useState('');

  // Filter recipes by name and labels (case-insensitive)
  const filterRecipes = (recipe: Recipe) => {
    const lowerSearch = searchFilter.toLowerCase();
    return (
      recipe.name.toLowerCase().includes(lowerSearch) ||
      (recipe.labels || []).some((label) => label.toLowerCase().includes(lowerSearch))
    );
  };

  // Sort by usage count (descending), then by name
  const sortRecipes = (a: Recipe, b: Recipe) => {
    const aCount = a.timesAddedAsMeal || 0;
    const bCount = b.timesAddedAsMeal || 0;
    if (bCount !== aCount) {
      return bCount - aCount; // Higher usage first
    }
    return a.name.localeCompare(b.name); // Alphabetical fallback
  };

  // Filter out excluded recipes and apply search + sort
  const filteredRecipes = props.recipes
    .filter((r) => !props.excludeRecipeIds?.includes(r.recipeId || ''))
    .filter(filterRecipes)
    .sort(sortRecipes);

  return (
    <Dialog open={props.open} onClose={props.handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon color="primary" />
          Favourite Recipes
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Search Box */}
        <TextField
          fullWidth
          label="Search recipes"
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Recipe List */}
        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
          {filteredRecipes.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No recipes found
            </Typography>
          )}

          {filteredRecipes.map((recipe) => (
            <ListItem
              key={recipe.recipeId}
              onClick={() => {
                props.onSelectRecipe(recipe);
                props.handleClose();
              }}
              sx={{
                cursor: 'pointer',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemAvatar>
                {recipe.img ? (
                  <Avatar src={recipe.img} />
                ) : (
                  <Avatar>
                    <RestaurantIcon />
                  </Avatar>
                )}
              </ListItemAvatar>

              <ListItemText
                primary={recipe.name}
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    {recipe.labels && recipe.labels.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {recipe.labels.slice(0, 2).join(', ')}
                      </Typography>
                    )}
                    {recipe.timesAddedAsMeal !== undefined && recipe.timesAddedAsMeal > 0 && (
                      <Chip
                        label={`${recipe.timesAddedAsMeal}x`}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={props.handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
